import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { Pass } from "ipctg-inflow-types/InFlowDB"
import { motion } from "motion/react"
import { useMemo, useState } from "react"
import { Key } from "react-aria-components"
import { useTranslation } from "react-i18next"

import Text from "@/globalComponents/atoms/Text"
import FilterTabs from "@/globalComponents/molecules/FilterTabs"
import { PurchasedQuotaPackCard } from "@/globalComponents/molecules/productCards/PurchasedQuotaPack"
import { parkPassesQueryOptions } from "@/utilities/hooks/queries/park"
import { useScopedNavigate } from "@/utilities/hooks/useScopedRouting"
import { isPassExpired, isPassFullyRedeemed } from "@/utilities/passes"

const PASS_TABS: Key[] = ["active", "expired"]

export const Route = createFileRoute("/_authenticated/account/passes/")({
  component: PassesIndexRouteComponent,
})

export function PassesIndexRouteComponent() {
  const { t } = useTranslation(["park"])
  const { data } = useSuspenseQuery(parkPassesQueryOptions())
  const { passes } = data
  const [selectedTab, setSelectedTab] = useState<Key>("active")
  const scopedNavigate = useScopedNavigate()

  const { activePasses, expiredPasses } = useMemo(() => {
    const active: Pass[] = []
    const expired: Pass[] = []

    passes.forEach((pass) => {
      if (isPassExpired(pass) || isPassFullyRedeemed(pass)) {
        expired.push(pass)
      } else {
        active.push(pass)
      }
    })

    return { activePasses: active, expiredPasses: expired }
  }, [passes])

  const displayedPasses =
    selectedTab === "active" ? activePasses : expiredPasses

  return (
    <>
      <FilterTabs onSelectionChange={setSelectedTab} selectedKey={selectedTab}>
        <FilterTabs.Tab id="active">{t("myPasses.tabs.active")}</FilterTabs.Tab>
        <FilterTabs.Tab id="expired">
          {t("myPasses.tabs.expired")}
        </FilterTabs.Tab>
      </FilterTabs>

      <FilterTabs.Panels tabOrder={PASS_TABS} selectedKey={selectedTab}>
        {displayedPasses.length === 0 ? (
          <Text style={{ textAlign: "center" }}>
            {selectedTab === "active"
              ? t("myPasses.noActivePasses")
              : t("myPasses.noExpiredPasses")}
          </Text>
        ) : (
          <motion.div
            initial="hidden"
            animate="visible"
            variants={{
              visible: { transition: { staggerChildren: 0.1 } },
            }}
          >
            {displayedPasses.map((pass) => (
              <motion.div
                key={pass.passID}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    transition: { ease: "easeIn" },
                  },
                }}
              >
                <PurchasedQuotaPackCard
                  {...pass}
                  action="viewDetails"
                  isPending={false}
                  onActionPressed={() =>
                    scopedNavigate({
                      to: "/account/passes/$passID",
                      params: { passID: pass.passID },
                    })
                  }
                />
              </motion.div>
            ))}
          </motion.div>
        )}
      </FilterTabs.Panels>
    </>
  )
}
